import type {
  CreateRoomInvitePayload,
  CreateRoomPayload,
  JoinRoomPayload,
  PaginationMeta,
  Participant,
  Room,
  RoomInviteLink,
  RoomListQuery,
  RoomWithDetails,
  UpdateRoomPayload,
} from '@matchroom/shared';
import { API_ROUTES } from '@matchroom/shared';
import { apiClient } from '@/lib/api-client';

export const roomService = {
  list: (params?: RoomListQuery) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.visibility) searchParams.set('visibility', params.visibility);
    if (params?.roomStatus) searchParams.set('roomStatus', params.roomStatus);
    if (params?.sort) searchParams.set('sort', params.sort);
    const qs = searchParams.toString();
    return apiClient.get<{ data: Room[]; meta: PaginationMeta }>(
      `${API_ROUTES.ROOMS.BASE}${qs ? `?${qs}` : ''}`,
    );
  },

  getById: (id: string) => apiClient.get<{ room: RoomWithDetails }>(API_ROUTES.ROOMS.BY_ID(id)),

  getByCode: (roomCode: string) =>
    apiClient.get<{ room: RoomWithDetails }>(API_ROUTES.ROOMS.BY_CODE(roomCode)),

  create: (payload: CreateRoomPayload) =>
    apiClient.post<{ room: RoomWithDetails }>(API_ROUTES.ROOMS.BASE, payload),

  update: (id: string, payload: UpdateRoomPayload) =>
    apiClient.patch<{ room: RoomWithDetails }>(API_ROUTES.ROOMS.BY_ID(id), payload),

  join: (id: string, payload?: JoinRoomPayload) =>
    apiClient.post<{ room: RoomWithDetails; participant: Participant }>(
      API_ROUTES.ROOMS.JOIN(id),
      payload ?? {},
    ),

  leave: (id: string) => apiClient.post<{ message: string }>(API_ROUTES.ROOMS.LEAVE(id)),

  delete: (id: string) => apiClient.delete<{ message: string }>(API_ROUTES.ROOMS.BY_ID(id)),

  createInvite: (id: string, payload?: CreateRoomInvitePayload) =>
    apiClient.post<{ invite: RoomInviteLink }>(
      API_ROUTES.ROOMS.INVITE(id),
      payload ?? {},
    ),

  transferHost: (roomId: string, newHostUserId: string) =>
    apiClient.post<{ room: RoomWithDetails }>(API_ROUTES.ROOMS.TRANSFER_HOST(roomId), {
      newHostUserId,
    }),

  removeParticipant: (roomId: string, userId: string) =>
    apiClient.post<{ room: RoomWithDetails | null; removedUserId: string }>(
      API_ROUTES.ROOMS.REMOVE_PARTICIPANT(roomId, userId),
    ),
};
